import {
  AuditLogAction,
  AuditService,
  CreditPackage,
  CreditsService,
  Feature,
  FeatureService,
  Group,
  GroupService,
  PricingTier,
  PricingTierService,
  SubscriptionPlan,
  SubscriptionPlanService,
  TUpdateUserRequest,
  User,
  UserService,
} from '@talon/web/lib/api';
import { makeObservable} from 'mobx';
import Storage from '@talon/web/src/utils/storage';
import { StorageProperties } from '@talon/web/src/types';
import { BaseStore } from './baseStore'; // for some dumb reason, these have to be imported individually
import { RootStore } from './rootStore'; // for some dumb reason, these have to be imported individually

export class AdminStore extends BaseStore {
  constructor(rootStore: RootStore) {
    super(rootStore);
    makeObservable(this, {});
  }

  // SUBSCRIPTION PLANS
  fetchSubscriptionPlans = async () => {
    try {
      const { subscriptionPlans } = await this.q(
        SubscriptionPlanService.listSubscriptionPlans(undefined, undefined, undefined, 'groups,pricing_tier,features')
      );
      return subscriptionPlans;
    } catch (e) {
      this.handleError(e);
    }
  };

  createSubscriptionPlan = async ({ name = 'New Subscription Plan', pricing_tier, groups, stripe_id, features }: Partial<SubscriptionPlan>) => {
    if (!name || !pricing_tier) {
      throw new Error('Name and price are required');
    }
    try {
      const { subscriptionPlan } = await this.q(
        SubscriptionPlanService.createSubscriptionPlan({
          name,
          pricing_tier_id: pricing_tier.id,
          group_ids: groups?.map((g) => g.id),
          stripe_id,
          feature_ids: features?.map((f) => f.id),
        })
      );
      return subscriptionPlan;
    } catch (e) {
      this.handleError(e);
    }
  };

  getSubscriptionPlan = async (id: string) => {
    try {
      const { subscriptionPlan } = await this.q(
        SubscriptionPlanService.getSubscriptionPlan(id, 'groups,pricing_tier,features')
      );
      return subscriptionPlan;
    } catch (e) {
      this.handleError(e);
    }
  };

  updateSubscriptionPlan = async (request: Partial<SubscriptionPlan> & Pick<SubscriptionPlan, 'id'>) => {
    try {
      const { subscriptionPlan } = await this.q(
        SubscriptionPlanService.updateSubscriptionPlan(request.id, {
          name: request.name,
          pricing_tier_id: request.pricing_tier?.id,
          group_ids: request.groups?.map((g) => g.id),
          feature_ids: request.features?.map((f) => f.id),
          stripe_id: request.stripe_id,
          is_public: request.is_public,
        })
      );
      if (!subscriptionPlan) {
        throw new Error('Failed to update Subscription Plan');
      }
      const updatedPlan = await this.getSubscriptionPlan(subscriptionPlan.id);
      return updatedPlan;
    } catch (e) {
      this.handleError(e);
    }
  };

  deleteSubscriptionPlan = async (id: string, suppressErrorToast = false) => {
    try {
      await this.q(SubscriptionPlanService.deleteSubscriptionPlan(id));
      return true;
    } catch (e) {
      if (!suppressErrorToast) this.handleError(e);
      return false;
    }
  };

  // PRICING TIERS
  fetchPricingTiers = async () => {
    try {
      const { pricingTiers } = await this.q(
        PricingTierService.listPricingTiers(undefined, undefined, undefined, 'subscription_plans')
      );
      return pricingTiers;
    } catch (e) {
      this.handleError(e);
    }
  };

  createPricingTier = async ({ name = 'New Pricing Tier', price = 0, stripe_id }: Partial<PricingTier>) => {
    if (isNaN(price)) {
      throw new Error('Price is required');
    }
    try {
      const { pricingTier } = await this.q(PricingTierService.createPricingTier({ name, price, stripe_id }));
      return pricingTier;
    } catch (e) {
      this.handleError(e);
    }
  };

  getPricingTier = async (id: string) => {
    try {
      const { pricingTier } = await this.q(PricingTierService.getPricingTier(id, 'subscription_plans'));
      return pricingTier;
    } catch (e) {
      this.handleError(e);
    }
  };

  updatePricingTier = async ({
    id,
    name,
    price,
    subscription_plans,
    stripe_id,
    storage_limit_bytes,
  }: Partial<PricingTier>) => {
    if (!id || (price !== undefined && isNaN(price))) {
      throw new Error('Valid id and price are required');
    }
    const request = {
      name,
      price,
      subscription_plan_ids: subscription_plans?.map((p) => p.id),
      stripe_id,
      // bigint arrives from the API as a string
      storage_limit_bytes:
        storage_limit_bytes === null || storage_limit_bytes === undefined
          ? storage_limit_bytes
          : Number(storage_limit_bytes),
    };
    try {
      await this.q(PricingTierService.updatePricingTier(id, request));
      const { pricingTier } = await this.q(PricingTierService.getPricingTier(id, 'subscription_plans'));
      return pricingTier;
    } catch (e) {
      this.handleError(e);
    }
  };

  deletePricingTier = async (id: string, suppressErrorToast = false) => {
    try {
      await this.q(PricingTierService.deletePricingTier(id));
      return true;
    } catch (e) {
      if (!suppressErrorToast) this.handleError(e);
      return false;
    }
  };

  // CREDIT PACKAGES
  fetchCreditPackages = async () => {
    try {
      const { creditPackages } = await this.q(CreditsService.listAllCreditPackages());
      return creditPackages;
    } catch (e) {
      this.handleError(e);
    }
  };

  createCreditPackage = async ({ name = 'New Credit Pack', hours = 0, is_active = true }: Partial<CreditPackage>) => {
    if (!name || !hours) {
      throw new Error('Name and hours are required');
    }
    try {
      const { creditPackage } = await this.q(CreditsService.createCreditPackage({ name, hours, is_active }));
      return creditPackage;
    } catch (e) {
      this.handleError(e);
    }
  };

  updateCreditPackage = async ({ id, name, hours, is_active }: Partial<CreditPackage>) => {
    if (!id) {
      throw new Error('Id is required');
    }
    try {
      const { creditPackage } = await this.q(
        CreditsService.updateCreditPackage(id, { name, hours, is_active })
      );
      return creditPackage;
    } catch (e) {
      this.handleError(e);
    }
  };

  deleteCreditPackage = async (id: string, suppressErrorToast = false) => {
    try {
      await this.q(CreditsService.deleteCreditPackage(id));
      return true;
    } catch (e) {
      if (!suppressErrorToast) this.handleError(e);
      return false;
    }
  };

  // FEATURES
  fetchFeatures = async () => {
    try {
      const { features } = await this.q(FeatureService.listFeatures());
      return features;
    } catch (e) {
      this.handleError(e);
    }
  };

  createFeature = async ({ name = 'New Feature' }: Partial<Feature>) => {
    try {
      const { feature } = await this.q(FeatureService.createFeature({ name }));
      return feature;
    } catch (e) {
      this.handleError(e);
    }
  };

  getFeature = async (id: string) => {
    try {
      const { feature } = await this.q(FeatureService.getFeature(id));
      return feature;
    } catch (e) {
      this.handleError(e);
    }
  };

  updateFeature = async ({ id, name }: Partial<Feature>) => {
    if (!id || !name) {
      throw new Error('Feature name is required');
    }
    try {
      const { feature } = await this.q(FeatureService.updateFeature(id, { name }));
      return feature;
    } catch (e) {
      this.handleError(e);
    }
  };

  deleteFeature = async (id: string, suppressErrorToast = false) => {
    try {
      await this.q(FeatureService.deleteFeature(id));
      return true;
    } catch (e) {
      if (!suppressErrorToast) this.handleError(e);
      return false;
    }
  };

  // USERS

  createUser = async ({ email, first_name, last_name, is_admin, is_registered, is_talent_manager, is_support_agent, groups }: Partial<User>) => {
    if (!email || !first_name || !last_name) {
      throw new Error('Email, first name, and last name are required');
    }
    try {
      const { user: newUser } = await this.q(UserService.createUser({ email, first_name, last_name, send_verification_email: false, is_admin, is_registered, is_talent_manager, is_support_agent, group_ids: groups?.map((g) => g.id), active_group_id: groups?.[0]?.id }));
      return newUser;
    } catch (e) {
      this.handleError(e);
    }
  };

  /** Sets another user's password (an admin doesn't need to know their current one) */
  setUserPassword = async (id: string, newPassword: string) => {
    try {
      await this.q(UserService.changePassword(id, { new_password: newPassword }));
      return true;
    } catch (e) {
      this.handleError(e);
      return false;
    }
  };

  fetchUsers = async ({ page = 1, limit, search }: { page?: number; limit?: number; search?: string } = {}) => {
    try {
      const { users, total } = await this.q(UserService.listUsers(limit, page, undefined, 'groups', search));
      return { items: users, total };
    } catch (e) {
      this.handleError(e);
    }
  };

  fetchUser = async (id: string) => {
    try {
      const { user } = await this.q(UserService.getUser(id, 'groups,active_group'));
      return user;
    } catch (e) {
      this.handleError(e);
    }
  };

  // Unbounded fetch for use in option pickers (e.g. group owner/members selects), not the paginated admin list
  fetchAllUsers = async () => {
    try {
      const { users } = await this.q(UserService.listUsers(undefined, undefined, undefined, 'groups'));
      return users;
    } catch (e) {
      this.handleError(e);
    }
  };

  updateUser = async (user: Partial<User>) => {
    if(!user.id) {
      throw new Error('User ID is required');
    }
    try {
      const { user: updatedUser } = await this.q(UserService.updateUser(user.id, {...user, group_ids: user.groups?.map((g) => g.id)} as TUpdateUserRequest));
      return updatedUser;
    } catch (e) {
      this.handleError(e);
    }
  };

  deleteUser = async (id: string, suppressErrorToast = false) => {
    try {
      await this.q(UserService.deleteUser(id));
      return true;
    } catch (e) {
      if (!suppressErrorToast) this.handleError(e);
      return false;
    }
  };

  // IMPERSONATION

  /**
   * Mints a session for `id` and switches the app to it. Stashes the admin's own current token
   * first so exitImpersonation() can restore it — only on success, so a failed call leaves the
   * admin's own session untouched.
   */
  impersonateUser = async (id: string): Promise<boolean> => {
    try {
      const currentToken = this.rootStore.userStore.token;
      const { jwt, user } = await this.q(UserService.impersonateUser(id));
      Storage.set(StorageProperties.ImpersonatorJwt, currentToken);
      this.rootStore.userStore.applySession(jwt, user);
      return true;
    } catch (e) {
      this.handleError(e);
      return false;
    }
  };

  // AUDIT LOG
  fetchAuditLog = async ({
    page = 1,
    limit,
    entityName,
    actorUserId,
    action,
    fromDate,
    toDate,
  }: {
    page?: number;
    limit?: number;
    entityName?: string;
    actorUserId?: string;
    action?: AuditLogAction;
    fromDate?: string;
    toDate?: string;
  } = {}) => {
    try {
      const { entries, total } = await this.q(
        AuditService.listAuditLog(entityName, actorUserId, action, fromDate, toDate, limit, page)
      );
      return { items: entries, total };
    } catch (e) {
      this.handleError(e);
    }
  };

  // GROUPS
  fetchGroups = async ({ page = 1, limit }: { page?: number; limit?: number } = {}) => {
    try {
      const { groups, total } = await this.q(
        GroupService.listGroups(limit, page, undefined, 'subscription_plan,owner,users')
      );
      return { items: groups, total };
    } catch (e) {
      this.handleError(e);
    }
  };

  createGroup = async ({ name, stripe_id, invited_emails, subscription_plan, users, projects, is_jpc_authorizer, type }: Partial<Group>) => {
    if (!name || !subscription_plan) {
      throw new Error('Name and subscription plan are required');
    }
    const request = {
      name,
      subscription_plan_id: subscription_plan.id,
      user_ids: users?.map((u) => u.id),
      project_ids: projects?.map((p) => p.id),
      stripe_id,
      invited_emails,
      is_jpc_authorizer,
      type,
    };
    try {
      const { group: newGroup } = await this.q(GroupService.createGroup(request));
      return newGroup;
    } catch (e) {
      this.handleError(e);
    }
  };

  getGroup = async (id: string, relations: string) => {
    try {
      const { group } = await this.q(GroupService.getGroup(id, relations));
      return group;
    } catch (e) {
      this.handleError(e);
    }
  };

  updateGroup = async (group: Partial<Group> & Pick<Group, 'id'>) => {
    try {
      if (Object.keys(group).some((key) => key !== 'id' && key !== 'settings')) {
        await this.q(GroupService.updateGroup(group.id, {
          name: group.name,
          type: group.type,
          subscription_plan_id: group.subscription_plan?.id,
          owner_id: group.owner?.id,
          user_ids: group.users?.map((u) => u.id),
          invited_emails: group.invited_emails,
          address: group.address,
          stripe_id: group.stripe_id,
          is_sag_signatory: group.is_sag_signatory,
          is_jpc_authorizer: group.is_jpc_authorizer,
        }));
      }
      if (group.settings) await this.q(GroupService.updateGroupSettings(group.id, group.settings));
      const updatedGroup =  await this.getGroup(group.id, 'subscription_plan,owner,users');
      return updatedGroup;
    } catch (e) {
      this.handleError(e);
    }
  };

  deleteGroup = async (id: string, suppressErrorToast = false) => {
    try {
      await this.q(GroupService.deleteGroup(id));
      return true;
    } catch (e) {
      if (!suppressErrorToast) this.handleError(e);
      return false;
    }
  };
}
